import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IAuthorizedUserData, IAuthResult } from '@project/view-models';
import { AuthorizationApiProvider } from '@project/data-providers';
import { LocalStorageService } from './local-storage.service';
import { tap } from 'rxjs/operators';
import { HcService } from './hc-service';

@Injectable({
  providedIn: 'root',
})
export class AuthorisationStateService {
  private _authorisationData$ = new BehaviorSubject<IAuthorizedUserData | null>(null);

  private readonly AUTH_DATA_STORAGE_TOKEN = 'auth';

  constructor(
    private authorizationApiProvider: AuthorizationApiProvider,
    private localStorageService: LocalStorageService,

    private hcService: HcService,
  ) {
    this.loadAuthData();
  }

  isAuthorized(): boolean {
    return !!this._authorisationData$.value;
  }

  setAuthorisationData(data: IAuthorizedUserData | null) {
    this._authorisationData$.next(data);
    this.saveAuthData();

    if (data?.healthCenterId) {
      this.hcService.getHcConfig();
    }
  }

  refreshToken(token: string) {
    this.setAuthorisationData({
      ...this.getAuthorizationData(),
      accessToken: token,
    });
  }

  getAuthorizationData(): IAuthorizedUserData | null {
    return this._authorisationData$.value;
  }

  getToken(): string {
    return this.getAuthorizationData()?.accessToken;
  }

  getRefreshToken(): string | undefined | null {
    return this.getAuthorizationData()?.refresh_token;
  }

  public getLiveQueueState(): boolean {
    return this.getAuthorizationData()?.is_live_queue_enabled ?? false;
  }

  private saveAuthData() {
    const authDataStr = JSON.stringify(this._authorisationData$.value);
    this.localStorageService.store(this.AUTH_DATA_STORAGE_TOKEN, authDataStr);
  }

  private loadAuthData() {
    let authData;
    try {
      authData = JSON.parse(this.localStorageService.getItem(this.AUTH_DATA_STORAGE_TOKEN));
    } catch {
      authData = null;
    }

    this._authorisationData$.next(authData);
  }

  impersonateHealthCenter(healthCenterId: string): Observable<IAuthResult> {
    return this.authorizationApiProvider.impersonateLogin(healthCenterId).pipe(
      tap((authData) => {
        this.setAuthorisationData(authData);
        this.localStorageService.store('logo-url', authData.health_center_logo_url || 'assets/logos/logo.svg');
      }),
    );
  }
}
